import { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { AuthContext } from '../utils/AuthContext';
import { computeCalendarHeaders } from '../utils/billSortGrouping';
import { downloadReportAsPDF } from '../utils/reportExport';
import ReportBillListItem from '../components/ReportBillListItem';
import './reportView.css';

function PageHeader({ report }) {
  const displayName = report?.org_name || report?.team_name || '';
  const logo = report?.org_logo;
  return (
    <div className="rv-header">
      <div className="rv-header-brand">
        {logo && <img src={logo} alt={displayName} className="rv-header-logo" />}
        <span className="rv-header-org-name">{displayName}</span>
      </div>
      <div className="rv-header-poweredby">
        Report provided by <strong>'elloGovna</strong>
      </div>
    </div>
  );
}

export default function ReportView() {
  const { token } = useParams();
  const { auth, individualSubscription, workplaceSubscription, accessLoading } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/reports/access/${token}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.msg || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, [token]);

  const canLinkToBillProfile = !accessLoading && auth.isAuthenticated && (individualSubscription || workplaceSubscription);

  const calendarHeaders = useMemo(
    () => data ? computeCalendarHeaders(data.bills, data.report.sort_mode) : new Map(),
    [data]
  );

  if (loading) return (
    <div className="rv-loading">
      <div className="spinner-border text-secondary" role="status" />
    </div>
  );

  if (error) return (
    <div className="rv-page">
      <div className="rv-shell">
        <PageHeader report={null} />
        <div className="rv-error">
          <i className="bi bi-exclamation-circle rv-error-icon" />
          <p>{error}</p>
        </div>
      </div>
    </div>
  );

  const { report, bills, contact } = data;

  return (
    <div className="rv-page">
      <div className="rv-shell">
        <PageHeader report={report} />

        <div className="rv-greeting">
          {contact && (
            <p className="rv-greeting-hello">
              Hello, {contact.first_name} {contact.last_name}
            </p>
          )}
          <div className="rv-title-row">
            <h1 className="rv-greeting-title">{report.title}</h1>
            <button type="button" className="rv-download-btn" onClick={() => downloadReportAsPDF(report, bills)}>
              <i className="bi bi-file-earmark-pdf me-1" />
              Download PDF
            </button>
          </div>
        </div>

        {report.intro_message && (
          <>
            <div
              className="rv-intro-message"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(report.intro_message) }}
            />
            <hr className="rv-intro-divider" />
          </>
        )}

        <p className="rv-greeting-sub">
          <strong>{report.org_name || report.team_name}</strong> has shared {bills.length} bill{bills.length !== 1 ? 's' : ''} with you
          {report.client_name ? <> for <strong>{report.client_name}</strong></> : null}.
        </p>

        <div className="rv-bill-list">
          {bills.map(bill => (
            <ReportBillListItem
              key={bill.bill_id}
              bill={bill}
              note={bill.report_note}
              showDigest={report.show_digest}
              showAiSummary={report.show_ai_summary}
              linkToProfile={canLinkToBillProfile}
              calendarInfo={calendarHeaders.get(bill.bill_id)}
            />
          ))}
        </div>

        {!auth.isAuthenticated && (
          <p className="rv-signin-note">
            <Link to="/">Sign in to 'elloGovna</Link> with an active subscription to view linked bill details.
          </p>
        )}
      </div>
    </div>
  );
}
